import { Control, Controller, RegisterOptions } from "react-hook-form";
import { useCallback, useMemo, useState } from "react";

import { NoxusInput } from "@/components/Form/InputField";
import { IODefinition } from "@/types/spotflow";
import { SpotToast } from "@/components/SpotToast";
import { findMaxStringValue } from "@/utils/generic";
import { ConfigDictEntry } from "@/types/api/spotflow/spotflow-config";
import {
  getTemplateStringValue,
  RichTextValue,
} from "@/components/Form/RichTextField";
import TiptapEditor from "@/components/Form/TiptapEditor/TiptapEditor";
import { Plus, Trash, ChevronUp } from "lucide-react";

import { NoxusFormLabel } from "@/components/Form/NoxusFormLabel";
import { Button } from "@/components/shadcn/button";
import { NoxusTooltip } from "@/components/UI/NoxusTooltip";
import { type AdvancedChipOption } from "@/components/Form/TiptapEditor/chipOptionsConfig";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/shadcn/accordion";
import { cn } from "@/lib/utils";

interface IComplexDictConfigProps {
  title?: string;
  subtitle?: string;
  value?: any;
  keys: ConfigDictEntry[];
  connector: IODefinition;
  tooltip?: string;
  onChange: (value: any[]) => void;
  nodeId: string;
  addNewDynamicOutput: (nodeId: string, connector_name: string) => any;
  removeDynamicOutput: (
    nodeId: string,
    key: string,
    connector_name: string
  ) => any;
  changeDynamicOutputName: (
    nodeId: string,
    key: string,
    newName: string,
    connector_name: string
  ) => any;
  unselectAllNodes?: () => any;
  isControlled?: boolean;
  options?: AdvancedChipOption[];
  name?: string;
  mode: "outputs" | "standalone";
  prefix_name?: string;
  isDisabled?: boolean;
  collapsible?: boolean;
}

const ComplexDict = (props: IComplexDictConfigProps) => {
  const connectors = props.connector.keys as string[];
  const [openItems, setOpenItems] = useState<string[]>(
    props.collapsible ? ["0"] : []
  );
  const cantDelete = useMemo(() => {
    if (props.mode === "standalone") {
      return props.value.length <= 1;
    }
    return connectors?.length <= 1;
  }, [connectors?.length, props.mode, props.value]);
  const {
    addNewDynamicOutput,
    removeDynamicOutput,
    changeDynamicOutputName,
    isDisabled,
  } = props;

  const handleAddClick = useCallback(() => {
    if (isDisabled) return;
    const controlling_entry = props.keys[0];
    if (props.mode === "outputs") {
      addNewDynamicOutput(props.nodeId, props.connector.name);
    }

    const prefix = props.prefix_name || "Output";
    const maxValue = findMaxStringValue(
      props.value.map((i: any) => i[controlling_entry.label]),
      new RegExp(`^${prefix} (\\d+)$`)
    );

    const new_value: Record<string, string> = {};
    props.keys.forEach((key, index) => {
      if (index === 0) {
        new_value[key.label] = `${prefix} ${maxValue + 1}`;
      } else {
        new_value[key.label] = "";
      }
    });
    const newDictArray = [...props.value, new_value];
    props.onChange(newDictArray);

    if (props.collapsible) {
      setOpenItems([newDictArray.length - 1 + ""]);
    }
  }, [addNewDynamicOutput, props, isDisabled]);

  const handleDeleteClick = useCallback(
    (indexToRemove: number) => {
      if (isDisabled) return;
      if (cantDelete) return;

      const keyToUseForName = props.keys[0]?.label || "Label";

      const name = props.value[indexToRemove][keyToUseForName];
      if (props.mode === "outputs") {
        removeDynamicOutput(props.nodeId, name, props.connector.name);
      }

      const newDictArray = [
        ...props.value
          .slice(0, indexToRemove)
          .concat(props.value.slice(indexToRemove + 1)),
      ];
      props.onChange(newDictArray);
    },
    [cantDelete, props, removeDynamicOutput, isDisabled]
  );

  const handleChange = useCallback(
    (value: RichTextValue | string, key: string, index: number) => {
      if (isDisabled) return;
      const newDictArray = [...props.value];
      newDictArray[index][key] = value;
      props.onChange(newDictArray);
    },
    [props, isDisabled]
  );

  const handleBlur = useCallback(
    (key: string, index: number) => {
      if (isDisabled) return;
      const cannotUpdate = connectors.some(
        (output, outputIndex) =>
          output === props.value[index][key] && index !== outputIndex
      );

      if (cannotUpdate) {
        const newDictArray = [...props.value];
        newDictArray[index][key] = connectors[index];
        props.onChange(newDictArray);

        SpotToast.error(`Can't have outputs with the same name`);
      } else {
        if (props.mode === "outputs") {
          changeDynamicOutputName(
            props.nodeId,
            connectors[index],
            props.value[index][key],
            props.connector.name
          );
        }
      }
    },
    [changeDynamicOutputName, connectors, props, isDisabled]
  );

  const renderEntryFields = (dict: any, dict_index: number) =>
    props.keys?.map(({ label, placeholder, has_chips }, key_index) => {
      if (props.collapsible && key_index === 0) {
        return null;
      }

      if (has_chips) {
        const parsedValue = dict[label]?.definition
          ? dict[label]
          : getTemplateStringValue(dict[label]);

        return (
          <div
            className="w-full"
            key={`dict-${dict_index}-${key_index}-${label}`}
          >
            <NoxusFormLabel label={label} name={props.name} />
            <TiptapEditor
              onChange={(e) => handleChange(e, label, dict_index)}
              value={parsedValue}
              label={label}
              chipOptions={props.options}
              unselectAllNodes={props.unselectAllNodes}
              placeholder={placeholder}
              isControlled={props.isControlled}
              isDisabled={isDisabled}
            />
          </div>
        );
      }
      const isTextArea = label == "Description and reasoning";
      return (
        <NoxusInput
          key={`dict-${dict_index}-${key_index}-${label}`}
          value={dict[label] || ""}
          label={label}
          forcedName={key_index === 0 ? "Empty" : undefined}
          onChange={(e) => handleChange(e.target.value, label, dict_index)}
          onBlur={() => {
            if (key_index === 0) {
              handleBlur(label, dict_index);
            }
          }}
          placeholder={placeholder}
          isTextarea={isTextArea}
          disable={isDisabled}
        />
      );
    });

  if (props.collapsible) {
    const firstKeyLabel = props.keys[0]?.label || "Name";

    return (
      <div className="flex flex-col">
        <p className="text-sm mb-1">{props.title}</p>
        {props.subtitle && (
          <p className="text-xs text-muted-foreground mb-1 leading-5">
            {props.subtitle}
          </p>
        )}
        <div className="flex flex-col gap-2 items-start">
          <Accordion
            type="multiple"
            value={openItems}
            onValueChange={setOpenItems}
            className="flex flex-col gap-3 w-full"
          >
            {props.value.map((dict: any, dict_index: number) => {
              const displayName =
                dict[firstKeyLabel] ||
                `${props.prefix_name || "Output"} ${dict_index + 1}`;
              const isOpen = openItems.includes(dict_index.toString());

              return (
                <AccordionItem
                  key={`dict-${dict_index}`}
                  value={dict_index.toString()}
                  className="group border rounded-md px-4 last:border-b first:border-t bg-[#FCFCFC]"
                >
                  <div className="flex items-center gap-2 [&>h3]:grow">
                    <AccordionTrigger
                      className="hover:no-underline py-3 grow items-center overflow-visible"
                      hideChevron
                    >
                      <div className="flex items-center justify-between w-full pr-2">
                        <NoxusInput
                          value={dict[firstKeyLabel] || ""}
                          forcedName="Empty"
                          onChange={(e) => {
                            e.stopPropagation();
                            handleChange(
                              e.target.value,
                              firstKeyLabel,
                              dict_index
                            );
                          }}
                          onBlur={() => handleBlur(firstKeyLabel, dict_index)}
                          placeholder={
                            props.keys[0]?.placeholder || "Route name"
                          }
                          disable={isDisabled}
                          customStyle="bg-transparent border-none shadow-none p-0 text-sm font-medium h-auto"
                        />
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <div className="hidden group-hover:flex items-center gap-2 h-6">
                          <NoxusTooltip
                            content={
                              cantDelete
                                ? props.mode === "outputs"
                                  ? "Can't have less than 1 value"
                                  : "Can't have less than 1 configuration"
                                : "Delete route"
                            }
                          >
                            <Button
                              variant="ghost"
                              size="iconsm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(dict_index);
                              }}
                              isDisabled={cantDelete || isDisabled}
                            >
                              <Trash className="size-4" strokeWidth={1.25} />
                            </Button>
                          </NoxusTooltip>
                        </div>
                        <Button variant="ghost" size="iconsm">
                          <ChevronUp
                            className={cn(
                              "transition-transform duration-200",
                              !isOpen ? "rotate-180" : ""
                            )}
                          />
                        </Button>
                      </div>
                    </AccordionTrigger>
                  </div>

                  <AccordionContent className="pb-4 pt-2 border-t">
                    <div className="flex flex-col gap-4">
                      {renderEntryFields(dict, dict_index)}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
          <Button
            variant="outline"
            onClick={handleAddClick}
            className="w-full"
            isDisabled={isDisabled}
          >
            <Plus size="1.25rem" strokeWidth="1.25px" />
            {`Add ${props.title?.toLowerCase() || "more"}`}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <p className="text-sm mb-1">{props.title}</p>
      {props.subtitle && (
        <p className="text-xs text-muted-foreground mb-1 leading-5">
          {props.subtitle}
        </p>
      )}
      <div className="flex flex-col gap-6 items-start">
        {props.value.map((dict: any, dict_index: number) => (
          <div
            key={`dict-${dict_index}`}
            className="w-full flex flex-col gap-4 items-end border rounded-lg p-4 relative"
          >
            {renderEntryFields(dict, dict_index)}

            <div className="absolute -top-3 -right-3">
              <NoxusTooltip
                placement="left"
                content={
                  cantDelete
                    ? props.mode === "outputs"
                      ? "Can't have less than 1 value"
                      : "Can't have less than 1 configuration"
                    : ""
                }
              >
                <Button
                  variant="ghost"
                  size="iconsm"
                  onClick={() => handleDeleteClick(dict_index)}
                  isDisabled={cantDelete || isDisabled}
                  className="bg-white rounded-full p-1 border border-gray-100 hover:border-blue-100 hover:bg-blue-50 hover:text-blue-950 disabled:text-gray-200 disabled:cursor-not-allowed disabled:bg-gray-50"
                >
                  <Trash className="size-4" strokeWidth={1.25} />
                </Button>
              </NoxusTooltip>
            </div>
          </div>
        ))}
        <Button
          variant="outline"
          onClick={handleAddClick}
          className="w-full"
          isDisabled={isDisabled}
        >
          <Plus size="1.25rem" strokeWidth="1.25px" />
          {`Add ${props.title?.toLowerCase() || "more"}`}
        </Button>
      </div>
    </div>
  );
};

interface IComplextDictConfigProps {
  name: string;
  tooltip?: string;
  label?: string;
  subtitle?: string;
  connector: IODefinition;
  rules?: RegisterOptions;
  control: Control<any>;
  onChangeExternal?: (action: string, options: any) => void;
  defaultValue: any[];
  nodeId: string;
  keys: ConfigDictEntry[];
  addNewDynamicOutput: (nodeId: string, connector_name: string) => any;
  removeDynamicOutput: (
    nodeId: string,
    key: string,
    connector_name: string
  ) => any;
  changeDynamicOutputName: (
    nodeId: string,
    key: string,
    newName: string,
    connector_name: string
  ) => any;
  unselectAllNodes?: () => any;
  isControlled?: boolean;
  options?: AdvancedChipOption[];
  mode: "outputs" | "standalone";
  prefix_name?: string;
  isDisabled?: boolean;
  collapsible?: boolean;
}

export const ComplexDictConfig = (props: IComplextDictConfigProps) => {
  return (
    <Controller
      control={props.control}
      name={props.name}
      rules={props.rules}
      defaultValue={props.defaultValue}
      render={({ field: { onChange, value } }) => {
        return (
          <ComplexDict
            value={value}
            title={props.label || "Fields"}
            subtitle={props.subtitle}
            keys={props.keys}
            connector={props.connector}
            tooltip={props.tooltip}
            onChange={onChange}
            nodeId={props.nodeId}
            addNewDynamicOutput={props.addNewDynamicOutput}
            removeDynamicOutput={props.removeDynamicOutput}
            changeDynamicOutputName={props.changeDynamicOutputName}
            unselectAllNodes={props.unselectAllNodes}
            isControlled={props.isControlled}
            options={props.options}
            name={props.name}
            mode={props.mode}
            prefix_name={props.prefix_name}
            isDisabled={props.isDisabled}
            collapsible={props.collapsible}
          />
        );
      }}
    />
  );
};
